import {
  CORRECTION_KEY,
  DOUBLE_ESCAPE_TIMEOUT_MS,
  GO_SEQUENCE_TIMEOUT_MS,
  goToTargetForKey,
  OVERLAY_DISMISS_COOLDOWN_MS,
  type GoToTarget,
} from "@components/keyboardShortcuts";
import { getOpenDismissableCount } from "@scribenote/brian-design-system";
import { useCallback, useEffect, useEffectEvent, useRef } from "react";

interface CompanionShortcutHandlers {
  onSearch: () => void;
  onHelp: () => void;
  onCorrect: () => void;
  onGoTo: (target: GoToTarget) => void;
  onSwitchUser: () => void;
}

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
};

/**
 * INVARIANT: every Escape-consuming overlay must be counted, or closing it counts toward Esc-Esc and signs the user out.
 * Kit `useDialog`/`usePopover` count themselves; anything else (or a manual popover that owns Escape) must call `registerDismissable`.
 * Still counted during the closing Escape: the browser's dismiss runs after this listener.
 */
const hasSelfDismissingOverlay = (): boolean => getOpenDismissableCount() > 0;

/** Escape counts toward Esc-Esc (switch user) only when nothing is open, so closing something never signs anyone out. */
export const useCompanionShortcuts = ({
  onSearch,
  onHelp,
  onCorrect,
  onGoTo,
  onSwitchUser,
}: CompanionShortcutHandlers): void => {
  const goPendingRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  // 0 means no buffered Escape.
  const lastEscRef = useRef(0);
  const lastOverlayDismissRef = useRef(0);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleKeyDown = useEffectEvent((e: KeyboardEvent) => {
    const disarm = () => {
      clearTimer();
      goPendingRef.current = false;
    };
    const resetEscBuffer = () => {
      lastEscRef.current = 0;
    };

    if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      disarm();
      resetEscBuffer();
      onSearch();
      return;
    }

    if (e.key === "Escape") {
      // Ignore a held Escape so it can't auto-repeat into a switch-user.
      if (e.repeat) return;

      if (goPendingRef.current) {
        disarm();
        resetEscBuffer();
        return;
      }

      if (hasSelfDismissingOverlay()) {
        lastOverlayDismissRef.current = Date.now();
        resetEscBuffer();
        return;
      }

      if (
        isEditableTarget(e.target) ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        e.shiftKey
      ) {
        resetEscBuffer();
        return;
      }

      const now = Date.now();

      // Roll the cooldown forward so a sustained mash needs a real pause before Esc-Esc.
      if (now - lastOverlayDismissRef.current < OVERLAY_DISMISS_COOLDOWN_MS) {
        lastOverlayDismissRef.current = now;
        resetEscBuffer();
        return;
      }

      if (now - lastEscRef.current < DOUBLE_ESCAPE_TIMEOUT_MS) {
        lastEscRef.current = 0;
        onSwitchUser();
        return;
      }
      lastEscRef.current = now;
      return;
    }

    // Shift stays allowed because `?` is Shift + `/`.
    if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;

    if (isEditableTarget(e.target)) return;

    // Esc-Esc must be consecutive.
    resetEscBuffer();

    if (goPendingRef.current) {
      disarm();
      const target = goToTargetForKey(e.key);
      if (target) {
        e.preventDefault();
        onGoTo(target);
      }
      return;
    }

    if (e.key.toLowerCase() === "g") {
      e.preventDefault();
      goPendingRef.current = true;
      clearTimer();
      timeoutRef.current = window.setTimeout(() => {
        goPendingRef.current = false;
        timeoutRef.current = null;
      }, GO_SEQUENCE_TIMEOUT_MS);
      return;
    }

    if (e.key.toLowerCase() === CORRECTION_KEY) {
      e.preventDefault();
      onCorrect();
      return;
    }

    if (e.key === "?") {
      e.preventDefault();
      onHelp();
    }
  });

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => handleKeyDown(e);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      clearTimer();
    };
  }, [clearTimer]);
};
